import React, { useRef, useCallback, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { FormHandles } from '@unform/core';
import { FiArrowLeft, FiSave } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { subDays, addHours, format } from 'date-fns';
import { toast } from 'react-toastify';

import api from 'services/api';
import getValidationErrors from 'services/getValidationErros';
import AppError from 'utils/AppError';
import { show } from 'store/modules/dialog/actions';

import { Emissora } from 'pages/@types';
import { Classificacoes, Permissao, StatusSolicitacao } from 'utils/variables';
import { SolicitacaoPromo } from './index';
import { interesses_url } from 'config/environment';
import { Cidades } from '../../utils/cidades';

import Button from 'components/Button';
import Input from 'components/Input';
import Card from 'components/Card';
import Select from 'components/Select';
import DatePicker from 'components/DatePicker';
import Radio from 'components/Radio';
import Checkbox from 'components/Checkbox';
import TextArea from 'components/TextArea';
import Tooltip from 'components/Tooltip';
import Spinner from 'components/Spinner';

import {
  Container,
  Content,
  Form,
  FormGroups,
  PromocaoInfo,
  VideoInfo,
} from './styles';
import { usePermit } from 'utils/hooks/usePermit';

// configuração do validator
const schema = Yup.object().shape({
  nome: Yup.string()
    .required('Campo Obrigatório')
    .matches(/^[^<>:"/\\|?*]+$/, {
      message: 'Nome contém caracteres especiais',
      excludeEmptyString: true,
    })
    .max(100, 'Nome exceu o limite de 100 caracteres'),
  datainicio: Yup.date()
    .required('Campo Obrigatório')
    .typeError('Data início inválida'),
  datainicioveiculacao: Yup.date()
    .required('Campo Obrigatório')
    .typeError('Data início da veiculação inválida'),
});
const schemaEnviada = Yup.object().shape({
  nome: Yup.string()
    .required('Campo Obrigatório')
    .matches(/^[^<>:"/\\|?*]+$/, {
      message: 'O título contém caracteres inválidos.',
      excludeEmptyString: true,
    })
    .max(100, 'Nome exceu o limite de 100 caracteres'),
  datainicio: Yup.date()
    .required('Campo Obrigatório')
    .typeError('Data início inválida'),
  datafim: Yup.date()
    .nullable()
    .when('tipomidia', {
      is: 'app',
      then: Yup.date()
        .required('Campo Obrigatório para solicitações com ações no APP')
        .typeError('Campo Obrigatório para solicitações com ações no APP'),
    }),
  datainicioveiculacao: Yup.date()
    .required('Campo Obrigatório')
    .typeError('Data início da veiculação inválida'),
  datafimveiculacao: Yup.date()
    .required('Campo Obrigatório')
    .typeError('Data fim da veiculação inválida'),
  eventorpc: Yup.string()
    .required('Campo Obrigatório')
    .nullable(),
  realizador: Yup.string().when('eventorpc', {
    is: 'parceiro',
    then: Yup.string().required('Campo Obrigatório'),
  }),
  tipomidia: Yup.string()
    .required('Campo Obrigatório')
    .nullable(),
  posevento: Yup.string()
    .required('Campo Obrigatório')
    .nullable(),
  acoesapp: Yup.string().when('tipomidia', {
    is: 'app',
    then: Yup.string().required('Campo Obrigatório'),
  }),
  appdescontoinfo: Yup.string().when('acoesapp', {
    is: acoesapp => acoesapp && acoesapp.includes('desconto'),
    then: Yup.string().required('Campo Obrigatório'),
  }),
  appqtcodigos: Yup.string().when('acoesapp', {
    is: acoesapp => acoesapp && acoesapp.includes('desconto'),
    then: Yup.string().required('Campo Obrigatório'),
  }),
  applinkcompra: Yup.string().when('acoesapp', {
    is: acoesapp => acoesapp && acoesapp.includes('desconto'),
    then: Yup.string().required('Campo Obrigatório'),
  }),
  emissora: Yup.string().required('Campo Obrigatório'),
  cidade: Yup.string().required('Campo Obrigatório'),
  local: Yup.string().required('Campo Obrigatório'),
  endereco: Yup.string().required('Campo Obrigatório'),
  eventopago: Yup.string()
    .required('Campo Obrigatório')
    .nullable(),
  valor: Yup.string().when('eventopago', {
    is: 'sim',
    then: Yup.string().required('Campo Obrigatório'),
  }),
  classificacao: Yup.string().required('Campo Obrigatório'),
  linkimg1: Yup.string().required('Campo Obrigatório'),
  linkvt1: Yup.string().when('tipomidia', {
    is: tipomidia => tipomidia !== 'site',
    then: Yup.string().required('Campo Obrigatório'),
  }),

  // TO DO: FAZER VALIDACAO CONNDICIONAL PARA REALIZADOR E VALOR DO EVENTO
});

interface IInteresses {
  name: string;
  values: string[];
}

export default function SolicitacaoPromoStore() {
  // react-router-dom
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [isEventoRPC, setIsEventoRPC] = useState(true);
  const [deadline, setDeadline] = useState<Date | null>(null);
  const [tipoMidia, setTipoMidia] = useState('site');
  const [acoesApp, setAcoesApp] = useState<string[]>([]);
  const [isEventoPago, setIsEventoPago] = useState(false);
  const [solicitanteId, setSolicitanteId] = useState(0);
  const [status, setStatus] = useState(StatusSolicitacao.EMPROCESSO);
  const [emissoras, setEmissoras] = useState([]);
  const [interesses, setInteresses] = useState<IInteresses[]>([]);
  const [initialData, setInitialData] = useState<Partial<SolicitacaoPromo>>({
    posevento: 'nao',
  });
  const [disabledForm, setDisabledForm] = useState(true);
  /**
   * USE PARAMS
   */
  const { solicitacaoId } = useParams<{ solicitacaoId: string }>();
  /**
   * REDUX
   */
  const { profile } = useSelector((state: any) => state.usuario);
  const dispatch = useDispatch();

  const hasSolPromoOutros = usePermit([], [Permissao.SOLPROMOOUTROS]);
  /**
   * USE REF
   */
  const ref = useRef<FormHandles>(null);

  /**
   * USE CALLBACK
   */
  const handleSuccessMessage = useCallback(
    ({ contentHTML, redirectTo }) => {
      const option = {
        type: 'success',
        confirmButtonText: '',
        contentHTML,
        timer: 3000,
        cancelButtonText: 'NÃO',
        redirectTo,
        showCancelButton: false,
        showConfirmButton: false,
        showCloseButton: true,
      };

      dispatch(show(option));
    },
    [dispatch],
  );

  const handleSubmit = useCallback(
    async data => {
      try {
        if (ref.current) {
          setLoadingSubmit(true);

          // Realiza validação de campos obrigatórios
          ref.current.setErrors({});
          if (status === StatusSolicitacao.ENVIADA)
            await schemaEnviada.validate(data, { abortEarly: false });
          else await schema.validate(data, { abortEarly: false });

          // Envia requisição de criação ou edição para o backend
          let response;
          const interessesJson = JSON.stringify(
            interesses.map(i => ({
              name: i.name,
              values: data[i.name],
            })),
          );
          interesses.forEach(i => {
            delete data[i.name];
          });

          if (solicitacaoId) {
            response = await api.put(`/solicitacaopromo/${solicitacaoId}`, {
              ...data,
              interesses: interessesJson,
              status: status,
              eventorpc: data.eventorpc === 'rpc',
              eventopago: data.eventopago === 'sim',
              posevento: data.posevento === 'sim',
              acoesapp: data.acoesapp ? data.acoesapp.join() : data.acoesapp,
            });
          } else {
            response = await api.post('/solicitacaopromo', {
              ...data,
              interesses: interessesJson,
              status: status,
              eventorpc: data.eventorpc === 'rpc',
              eventopago: data.eventopago === 'sim',
              posevento: data.posevento === 'sim',
              acoesapp: data.acoesapp ? data.acoesapp.join() : data.acoesapp,
            });
          }
          // Redireciona usuário em caso de sucesso
          if (response && response.data)
            handleSuccessMessage({
              contentHTML: `<h3>Solicitação salva!</h3> <p>Você será redirecionado para a lista de solicitações em 3 segundos</p>`,
              redirectTo: `/solicitacaopromo`,
            });
          else toast.error('Falha ao salvar a solicitação');
        }
      } catch (err) {
        console.log(err);
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          if (ref.current) ref.current.setErrors(errors);
          toast.error(err.message);
        } else if (err instanceof Error) {
          toast.error(err.message);
        } else {
          const appError = err as AppError;
          toast.error(appError.message);
        }
      }
      setLoadingSubmit(false);
    },
    [handleSuccessMessage, interesses, solicitacaoId, status],
  );

  /**
   * USE EFFECT
   */
  useEffect(() => {
    async function loadEmissoras() {
      await api
        .get('/emissoras')
        .then(response => {
          if (response && response.data) {
            const emissorasNormalized = response.data.map((emi: Emissora) => ({
              value: emi.nome,
              label: emi.nome,
            }));
            emissorasNormalized.unshift({ value: 'REDE', label: 'REDE' });
            setEmissoras(emissorasNormalized);
          }
        })
        .catch(error => {
          if (error instanceof Error) {
            toast.error(error.message);
          } else console.log(error);
        });
    }

    loadEmissoras();
  }, []);

  useEffect(() => {
    async function loadInteresses() {
      try {
        const response = await axios.get(interesses_url);
        if (response && response.data && response.data.interests)
          setInteresses(response.data.interests);
        else toast.error('Falha ao carregar Interesses');
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message);
        } else {
          const appError = error as AppError;
          toast.error(appError.message);
        }
      }
    }
    loadInteresses();
  }, []);

  useEffect(() => {
    async function loadSolicitacao() {
      setLoading(true);
      try {
        const response = await api.get(`/solicitacaopromo/${solicitacaoId}`);

        if (response && response.data) {
          setIsEventoRPC(response.data.eventorpc);
          setTipoMidia(response.data.tipomidia);
          setIsEventoPago(response.data.eventopago);
          setStatus(response.data.status);
          setAcoesApp(
            response.data.acoesapp ? response.data.acoesapp.split(',') : [],
          );

          const _initialData = {
            ...response.data,
            acoesapp: response.data.acoesapp
              ? response.data.acoesapp.split(',')
              : [],
            eventorpc: response.data.eventorpc ? 'rpc' : 'parceiro',
            eventopago: response.data.eventopago ? 'sim' : 'nao',
            posevento: response.data.posevento ? 'sim' : 'nao',
            ...(response.data.classificacao && {
              classificacao: {
                value: response.data.classificacao,
                label: response.data.classificacao,
              },
            }),
            ...(response.data.emissora && {
              emissora: {
                value: response.data.emissora,
                label: response.data.emissora,
              },
            }),
            ...(response.data.cidade && {
              cidade: {
                value: response.data.cidade,
                label: response.data.cidade,
              },
            }),
            ...(response.data.datainicio && {
              datainicio: addHours(new Date(response.data.datainicio), 3),
            }),
            ...(response.data.datainicioveiculacao && {
              datainicioveiculacao: addHours(
                new Date(response.data.datainicioveiculacao),
                3,
              ),
            }),
            ...(response.data.datafim && {
              datafim: addHours(new Date(response.data.datafim), 3),
            }),
            ...(response.data.datafimveiculacao && {
              datafimveiculacao: addHours(
                new Date(response.data.datafimveiculacao),
                3,
              ),
            }),
            ...(response.data.appdatapromocao && {
              appdatapromocao: addHours(
                new Date(response.data.appdatapromocao),
                3,
              ),
            }),
          };

          // Estamos percorrendo os Interesses dessa forma por ser um JSON e não encontramos outra forma no momento, assim colocando o initialData em uma const,
          // para fazer um forEach de cada Objeto: interesse(name) e seus values. Por ser também dinâmico por consumir uma api/json,
          // e os values e labels serem iguais, utilizamos o v; assim como foi feito no Select.
          // Por exemplo: [{"name":"Consumos na TV","values":[]},{"name":"Hobbies e Interesses","values":["Assistir TV"]}

          const jsonInteresses = JSON.parse(response.data.interesses);
          jsonInteresses.forEach((i: IInteresses) => {
            _initialData[i.name] = i.values.map(v => ({
              value: v,
              label: v,
            }));
          });
          setInitialData(_initialData);
        }
        setSolicitanteId(response.data.solicitante.id);
        setLoading(false);
      } catch (e) {
        if (e instanceof Error) toast.error(e.message);
        else if (e instanceof AppError) toast.error(e.message);
        else {
          const appError = e as AppError;
          if (appError.message) toast.error(appError.message);
          else toast.error('Erro inesperado');
        }
      }
    }
    if (solicitacaoId) {
      loadSolicitacao();
    }
  }, [interesses, solicitacaoId]);

  useEffect(() => {
    if (
      !solicitacaoId ||
      (initialData.status === StatusSolicitacao.EMPROCESSO &&
        !loadingSubmit &&
        (solicitanteId === profile.id || hasSolPromoOutros))
    )
      setDisabledForm(false);
    else setDisabledForm(true);
  }, [
    hasSolPromoOutros,
    initialData.status,
    loadingSubmit,
    profile.id,
    profile.permissao,
    solicitacaoId,
    solicitanteId,
  ]);

  return (
    <Container>
      <Card height="auto">
        {!loading && !!emissoras && emissoras.length > 0 && (
          <Form ref={ref} onSubmit={handleSubmit} initialData={initialData}>
            <Content>
              <header>
                <h4>SOLICITAÇÕES - PROMOCIONAIS</h4>
              </header>
              <FormGroups $columns="4fr 1fr 1fr 1fr">
                <div>
                  <section>
                    <h5>Nome do Evento (o título que vamos divulgar) *</h5>
                  </section>
                  <Input
                    name="nome"
                    max={100}
                    placeholder="Nome do Evento *"
                    disabled={disabledForm}
                  />
                </div>
                <div>
                  <section>
                    <h5>Início/Data da Ação *</h5>
                  </section>
                  <DatePicker
                    placeholderText="DE"
                    minDate={new Date()}
                    name="datainicio"
                    disabled={disabledForm}
                  />
                </div>
                <div>
                  <section id="datafinal">
                    <h5>Data Final</h5>
                  </section>

                  <DatePicker
                    placeholderText="ATÉ"
                    minDate={new Date()}
                    name="datafim"
                    disabled={disabledForm}
                  />
                  <Tooltip target="datafinal" position="bottom">
                    Para festivais, feiras e similares
                  </Tooltip>
                </div>
                <div>
                  <section>
                    <h5>Horário *</h5>
                  </section>
                  <Input
                    name="horario"
                    max={45}
                    placeholder="Horário *"
                    disabled={disabledForm}
                  />
                </div>
              </FormGroups>
              <FormGroups $columns="1fr 1fr 2fr 3fr">
                <div>
                  <section>
                    <h5>Data início veiculação *</h5>
                  </section>
                  <DatePicker
                    placeholderText="DE"
                    minDate={new Date()}
                    name="datainicioveiculacao"
                    disabled={disabledForm}
                    onSelect={(value: Date) => {
                      setDeadline(value);
                    }}
                    errorField={
                      deadline
                        ? subDays(deadline, 8) < new Date()
                          ? 'O envio da solicitação deve ser imediato'
                          : `Enviar solicitação até ${format(
                              subDays(deadline, 9),
                              'dd/MM/yyy',
                            )}`
                        : null
                    }
                  />
                </div>
                <div>
                  <section>
                    <h5>Data fim veiculação</h5>
                  </section>
                  <DatePicker
                    placeholderText="ATÉ"
                    minDate={new Date()}
                    name="datafimveiculacao"
                    disabled={disabledForm}
                  />
                </div>
                <div>
                  <section>
                    <h5>Evento RPC ou parceiro *</h5>
                  </section>
                  <Radio
                    name="eventorpc"
                    options={[
                      { id: 'rpc', label: 'RPC' },
                      { id: 'parceiro', label: 'PARCEIRO' },
                    ]}
                    handleSelected={value => setIsEventoRPC(value.id === 'rpc')}
                    disabled={disabledForm}
                  />
                </div>
                {!isEventoRPC && (
                  <div>
                    <section>
                      <h5>Quem é o realizador? *</h5>
                    </section>
                    <Input
                      id="realizador"
                      name="realizador"
                      max={100}
                      placeholder="Nome do Realizador *"
                      disabled={disabledForm}
                    />
                    <Tooltip target="realizador" position="bottom">
                      Lembre-se de preencher corretamente, da maneira que deverá
                      aparecer no VT e demais peças
                    </Tooltip>
                  </div>
                )}
              </FormGroups>
              <div>
                <div>
                  <section>
                    <h5>Qual é a mídia para promover esse evento? *</h5>
                  </section>
                  <Radio
                    name="tipomidia"
                    options={[
                      { id: 'site', label: 'Somente Matéria no Site' },
                      {
                        id: 'vt',
                        label: 'VT na programação e Matéria no Site',
                      },
                      {
                        id: 'app',
                        label: 'VT + Matéria no Site + Ação no app',
                      },
                    ]}
                    handleSelected={value => {
                      setAcoesApp(prevState =>
                        value.id !== 'app' ? [] : prevState,
                      );
                      setTipoMidia(value.id);
                    }}
                    disabled={disabledForm}
                  />
                </div>
              </div>
              <div>
                {tipoMidia === 'app' && (
                  <div>
                    <section>
                      <h5>Qual é a ação no APP Você na RPC? *</h5>
                    </section>
                    <Checkbox
                      columns="1fr 1fr"
                      name="acoesapp"
                      options={[
                        { id: 'desconto', label: 'Desconto para usuários' },
                        {
                          id: 'ingresso',
                          label:
                            'Promoção - Distribuição Gratuita de Ingressos',
                        },
                        {
                          id: 'banner',
                          label: 'Somente banner de divulgação',
                        },
                        {
                          id: 'outro',
                          label: 'Outra ação, definida previamente',
                        },
                      ]}
                      handleSelected={value => {
                        if (value.checked) {
                          setAcoesApp(prevState => [...prevState, value.id]);
                        } else {
                          setAcoesApp(prevState =>
                            prevState.filter(p => p !== value.id),
                          );
                        }
                      }}
                      disabled={disabledForm}
                    />
                  </div>
                )}
              </div>
              {acoesApp.includes('ingresso') && (
                <div>
                  <section>
                    <h5>Promoção de ingressos no App</h5>
                  </section>
                  <PromocaoInfo>
                    <div>
                      <Input
                        id="appqtingressos"
                        name="appqtingressos"
                        placeholder="Especifique a quantidade de ingressos e para qual área se aplica"
                        disabled={disabledForm}
                      />
                      <Tooltip target="appqtingressos" position="bottom">
                        Exemplo: 200 para camarote, 100 para pista...
                      </Tooltip>
                    </div>

                    <div>
                      <DatePicker
                        placeholderText="Promoção"
                        minDate={new Date()}
                        name="appdatapromocao"
                        disabled={disabledForm}
                      />
                    </div>
                  </PromocaoInfo>
                  <TextArea
                    name="appvtingressos"
                    height="70"
                    placeholder="Com isso, teremos um VT adicional, falando somente da ação de distribuição de ingressos.
                  Se houver algo específico a incluir nesta peça, preencha o campo abaixo."
                    icon=""
                    errorField=""
                    disabled={disabledForm}
                  />
                </div>
              )}
              {acoesApp.includes('desconto') && (
                <>
                  <div>
                    <section>
                      <h5>Desconto no App *</h5>
                    </section>
                    <Input
                      id="appdescontoinfo"
                      name="appdescontoinfo"
                      placeholder="Especifique qual é a porcentagem desconto e para qual área se aplica *"
                      disabled={disabledForm}
                    />
                    <Tooltip target="appdescontoinfo" position="bottom">
                      Exemplo: 15% para pista, 30% para camarote...
                    </Tooltip>
                  </div>

                  <FormGroups $columns="1fr 2fr">
                    <Input
                      name="appqtcodigos"
                      placeholder="Quantidade de códigos que serão gerados *"
                      disabled={disabledForm}
                    />

                    <Input
                      name="applinkcompra"
                      placeholder="Indique o link para compra de ingressos *"
                      disabled={disabledForm}
                    />
                  </FormGroups>
                </>
              )}
              {acoesApp.includes('banner') && (
                <div>
                  <section>
                    <h5>Banner no App</h5>
                  </section>
                  <TextArea
                    name="appartebanner"
                    height="70"
                    placeholder="Informações que deverão ser destacadas na arte do BANNER"
                    icon=""
                    errorField=""
                    disabled={disabledForm}
                  />
                </div>
              )}
              {acoesApp.includes('outro') && (
                <div>
                  <section>
                    <h5>Outra ação no App</h5>
                  </section>
                  <TextArea
                    name="appoutracao"
                    height="70"
                    placeholder="Descreva como será a outra ação no App"
                    icon=""
                    errorField=""
                    disabled={disabledForm}
                  />
                </div>
              )}
              <FormGroups $columns="1fr 1fr 1fr 1fr">
                <div>
                  <section>
                    <h5>Praça *</h5>
                  </section>
                  <Select
                    id="emissora"
                    errorField=""
                    name="emissora"
                    placeholder="Emissora"
                    options={emissoras}
                    isSearchable
                    fill={false}
                    disabled={disabledForm}
                  />
                  <Tooltip target="emissora" position="right">
                    Para sabermos qual o direcionamento da comunicação e onde
                    será a veiculação.
                  </Tooltip>
                </div>
                <div>
                  <section>
                    <h5>Cidade *</h5>
                  </section>
                  <Select
                    id="cidade"
                    errorField=""
                    name="cidade"
                    placeholder="Cidade"
                    options={Cidades.map(c => ({ value: c, label: c }))}
                    isSearchable
                    fill={false}
                    disabled={disabledForm}
                  />
                </div>
                <div>
                  <section>
                    <h5>Local *</h5>
                  </section>
                  <Input
                    name="local"
                    max={100}
                    placeholder="Nome do espaço / teatro / praça (etc)"
                    disabled={disabledForm}
                  />
                </div>
                <div>
                  <section>
                    <h5>Endereço *</h5>
                  </section>
                  <Input
                    name="endereco"
                    max={100}
                    placeholder="Endereço *"
                    disabled={disabledForm}
                  />
                </div>
              </FormGroups>
              <section>
                <h5>Conta mais sobre o evento!</h5>
              </section>
              <FormGroups $columns="1fr 1fr">
                <div>
                  <TextArea
                    id="eventoinfo"
                    name="eventoinfo"
                    height="70"
                    placeholder="Sobre - o que estamos apoiando / promovendo? *"
                    icon=""
                    errorField=""
                    disabled={disabledForm}
                  />
                  <Tooltip target="eventoinfo" position="bottom">
                    Descreva brevemente do que se trata o evento, o que é
                    destaque nesse dia, qual a relevância e o público que vamos
                    conversar.
                  </Tooltip>
                </div>
                <div>
                  <TextArea
                    name="atracoes"
                    height="70"
                    placeholder="Principais Atrações *"
                    icon=""
                    errorField=""
                    disabled={disabledForm}
                  />
                </div>
              </FormGroups>
              <FormGroups $columns="1fr 1fr 1fr 1fr">
                <div>
                  <section>
                    <h5>Classificação *</h5>
                  </section>
                  <Select
                    errorField=""
                    name="classificacao"
                    placeholder="Classificação *"
                    options={Classificacoes}
                    isSearchable
                    fill={false}
                    disabled={disabledForm}
                  />
                </div>
                <div>
                  <section>
                    <h5>Vamos criar um VT de pós-evento? *</h5>
                  </section>
                  <Radio
                    name="posevento"
                    options={[
                      { id: 'sim', label: 'SIM' },
                      { id: 'nao', label: 'NÃO' },
                    ]}
                    disabled={disabledForm}
                  />
                </div>
                <div>
                  <section>
                    <h5>O evento é pago? *</h5>
                  </section>
                  <Radio
                    name="eventopago"
                    options={[
                      { id: 'sim', label: 'SIM' },
                      { id: 'nao', label: 'NÃO' },
                    ]}
                    handleSelected={value =>
                      setIsEventoPago(value.id === 'sim')
                    }
                    disabled={disabledForm}
                  />
                </div>

                {isEventoPago && (
                  <div>
                    <section>
                      <h5>Qual o valor do ingresso *</h5>
                    </section>
                    <TextArea
                      name="valor"
                      height="70"
                      icon=""
                      errorField=""
                      placeholder="Valor *"
                      disabled={disabledForm}
                    />
                  </div>
                )}
              </FormGroups>

              <div>
                <section>
                  <h5>Teremos ativações da marca RPC? Como será essa ação?</h5>
                </section>
                <TextArea
                  name="ativacoes"
                  height="70"
                  placeholder=""
                  icon=""
                  errorField=""
                  disabled={disabledForm}
                />
              </div>
              <section>
                <h5>Sites e Links úteis:</h5>
              </section>
              <FormGroups $columns="1fr 1fr 1fr 1fr">
                <div>
                  <Input
                    name="siteoficial"
                    placeholder="Site Oficial"
                    disabled={disabledForm}
                  />
                </div>
                <div>
                  <Input
                    name="redesocial"
                    placeholder="Perfis de Redes Sociais"
                    disabled={disabledForm}
                  />
                </div>
                <div>
                  <Input
                    name="outroslinks"
                    placeholder="Outros links"
                    disabled={disabledForm}
                  />
                </div>
                <div>
                  <Input
                    name="releaseoficial"
                    placeholder="Realease Oficial (somente links)"
                    disabled={disabledForm}
                  />
                </div>
              </FormGroups>
              <section>
                <h5>Links para imagens: *</h5>
              </section>
              <FormGroups $columns="1fr 1fr 1fr">
                <div>
                  <Input
                    name="linkimg1"
                    placeholder="Link para imagens 1"
                    disabled={disabledForm}
                  />
                </div>
                <div>
                  <Input
                    name="linkimg2"
                    placeholder="Link para imagens 2"
                    disabled={disabledForm}
                  />
                </div>
                <div>
                  <Input
                    name="linkimg3"
                    placeholder="Link para imagens 3"
                    disabled={disabledForm}
                  />
                </div>
              </FormGroups>
              {(tipoMidia === 'vt' || tipoMidia === 'app') && (
                <>
                  <VideoInfo>
                    <p>Como devem ser os vídeos? </p>
                    <ul>
                      <li>Orientação: horizontal</li>
                      <li>Resolução: no mínimo 1920 x 1080</li>
                      <li>
                        Duração: de 1 a 2 minutos (pode ser um vídeo promocional
                        já existente ou, ainda, um clipe com imagens diversas)
                      </li>
                      <li>Formato: MP4</li>
                    </ul>
                    <p>Importante! O vídeo deve:</p>
                    <ul>
                      <li>
                        Ser limpo (ou seja, sem letterings (texto), aplicações
                        de arte ou colorização).
                      </li>
                      <li>
                        Em caso de clipes e shows, deve ter o áudio original das
                        músicas (sem locução/voz de locutor/comunicador).
                      </li>
                    </ul>
                    <footer>
                      <p>
                        Os vídeos podem ser enviados via WeTransfer (cuidando
                        sempre com a data, para que o link não expire), Google
                        Drive ou OneDrive - Sharepoint
                      </p>
                      <p>Não podemos utilizar vídeos enviados por WhatsApp.</p>
                      <p>
                        Não podemos baixar vídeos do YouTube/Vimeo/Outras
                        plataformas, portanto, pedimos o arquivo de clipes e
                        shows.
                      </p>
                    </footer>
                  </VideoInfo>
                  <section>
                    <h4>Links para VT:</h4>
                  </section>
                  <FormGroups $columns="1fr 1fr 1fr">
                    <div>
                      <Input
                        name="linkvt1"
                        placeholder="Link para VT 1 *"
                        disabled={disabledForm}
                      />
                    </div>
                    <div>
                      <Input
                        name="linkvt2"
                        placeholder="Link para VT 2"
                        disabled={disabledForm}
                      />
                    </div>
                    <div>
                      <Input
                        name="linkvt3"
                        placeholder="Link para VT 3"
                        disabled={disabledForm}
                      />
                    </div>
                  </FormGroups>
                </>
              )}
              <div>
                <section>
                  <h5>Tem alguma informação adicional?</h5>
                </section>
                <TextArea
                  name="infoadicional"
                  id="infoadicional"
                  height="70"
                  placeholder=""
                  icon=""
                  errorField=""
                  disabled={disabledForm}
                />
                <Tooltip target="infoadicional" position="bottom">
                  Aqui vão informações <b>complementares</b> ao que você já
                  citou anteriormente. <br />
                  <b>Por exemplo:</b> o que destacar no VT ou na matéria?
                  <br />
                  Estamos aguardando alguma outra informação do parceiro?
                  <br />
                  É necessário validar algo antes da publicação?
                  <br />
                  Por favor, não adicione o release neste campo.
                </Tooltip>
              </div>

              <div>
                <section>
                  <h5>Telefone para contato:</h5>
                </section>
                <div>
                  <Input
                    name="telefone"
                    placeholder="Telefone"
                    defaultValue={
                      initialData.telefone || profile.telefone || null
                    }
                    max={45}
                    disabled={disabledForm}
                  />
                </div>
              </div>
              <section>
                <h5>Interesses</h5>
              </section>
              <FormGroups $columns="1fr 1fr 1fr 1fr">
                {interesses &&
                  interesses.map((interesse, i) => {
                    return (
                      <div
                        key={`interesse${i}`}
                        style={{ margin: '0 0.3rem 0.6rem' }}
                      >
                        <Select
                          name={interesse.name}
                          placeholder={interesse.name}
                          options={interesse.values.map(v => ({
                            value: v,
                            label: v,
                          }))}
                          isSearchable
                          isMulti
                          fill={false}
                        />
                      </div>
                    );
                  })}
              </FormGroups>
            </Content>
            <div className="ButtonGroup">
              <Button
                color="primary"
                onClick={e => {
                  e.preventDefault();
                  navigate(-1);
                }}
              >
                <FiArrowLeft />
                VOLTAR
              </Button>
              <div style={{ display: 'flex' }}>
                <Button
                  className="submitButton"
                  type="submit"
                  color="primary"
                  onClick={() => setStatus(StatusSolicitacao.EMPROCESSO)}
                  disabled={disabledForm}
                >
                  <FiSave />
                  {!loadingSubmit ? 'SALVAR' : <Spinner type="ThreeDots" />}
                </Button>
                <Button
                  className="submitEnviadaButton"
                  type="submit"
                  color="success"
                  onClick={() => setStatus(StatusSolicitacao.ENVIADA)}
                  disabled={disabledForm}
                >
                  <FiSave />
                  {!loadingSubmit ? (
                    'SALVAR E ENVIAR'
                  ) : (
                    <Spinner type="ThreeDots" />
                  )}
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Card>
    </Container>
  );
}
